import { CloseIcon, EditIcon, SearchIcon, ViewIcon, DeleteIcon, WarningTwoIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  DrawerBody,
  DrawerHeader,
  Flex,
  HStack,
  Heading,
  IconButton,
  Image,
  Input,
  InputGroup, InputRightElement,
  Link,
  Text,
  VStack,
  useDisclosure,
  useToast
} from "@chakra-ui/react";
import { createColumnHelper } from "@tanstack/react-table";
import AppViewDrawer from "components/AppViewDrawer";
import AppViewListItem from "components/AppViewListItem";
import { Pagination } from "components/Pagination";
import { useApi } from "hooks/useApi";
import { useAuthentication } from "hooks/useAuthentication";
import { useCache } from "hooks/useCache";
import { useCallback, useContext, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Store } from "store";
import { showToast, toastTypes } from "utils";
import { ReactTable } from "../../components/ReactTable";

type Table3RowType = {
  id: number;
  name: string;
  rate: string;
  amount: string;
  tax_frequency_name: string;
};

export default function TaxNatureManagementSection() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isConfirmOpen,
    onOpen: onConfirmOpen,
    onClose: onConfirmClose,
  } = useDisclosure();
  const cancelRef = useRef<any>();
  const [activeItem, setActiveItem] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const toast = useToast();
  const { dispatch } = useContext(Store);
  const navigate = useNavigate();
  const {hasPermission, user} = useAuthentication()
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchBarValue34, setSearchBarValue34] = useState("");
  const { getTaxNatures, deleteTaxNature } = useApi();
  const { data: taxNatures, mutate } = useCache(
    "taxNatures",
    () => getTaxNatures(page, searchBarValue34, String(user?.user?.institution_id || ""))
  );
  const handleSearch = (event: any) => {
    const value = event.target.value
    mutate(getTaxNatures(page,value))
  };
  const handleClearSearch = (event: any) => {
    setSearchBarValue34("");
    mutate(getTaxNatures(page,""))
  }

  const handleJumpToPage = (pg: number) => {
    setPage(pg);
    mutate(getTaxNatures(pg));
  };
  const handleSetNextPage = () => {
    if (taxNatures.length > 0 && taxNatures[0].num_of_pages >= page + 1) {
      setPage((prev: any) => prev + 1);
      mutate(getTaxNatures(page + 1));
    }
  };
  const handleSetPrevPage = () => {
    if (taxNatures.length > 0 && page > 0) {
      setPage((prev: any) => prev - 1);
      mutate(getTaxNatures(page + 1));
    }
  };

  const handleViewItem = useCallback(
    (item: any) => {
      setActiveItem(item);
      onOpen();
    },
    [onOpen, setActiveItem]
  );

  const table3Columns = useMemo(() => {
    const table3ColumnHelper = createColumnHelper<Table3RowType>();
    return [
      table3ColumnHelper.accessor("name", {
        cell: (info) => (
          <Flex px={{ base: "20px", sm: "24px" }}>
            <Text>{info.getValue<string>()}</Text>
          </Flex>
        ),
        header: (info) => (
          <Flex p="14px" px={"40px"}>
            <Heading as="h6">Nature de règlement</Heading>
          </Flex>
        ),
        meta: { width: "172px" },
      }),
      table3ColumnHelper.accessor("amount", {
        cell: (info) => (
          <Flex px={{ base: "20px", sm: "24px" }}>
            <Text>{info.getValue<string>()}</Text>
          </Flex>
        ),
        header: (info) => (
          <Flex p="14px" px={"40px"}>
            <Heading as="h6">Montant de la créance</Heading>
          </Flex>
        ),
        meta: { width: "100px" },
      }),
      table3ColumnHelper.accessor("rate", {
        cell: (info) => (
          <Flex px={{ base: "20px", sm: "24px" }}>
            <Text>{info.getValue<string>()}</Text>
          </Flex>
        ),
        header: (info) => (
          <Flex p="14px" px={"40px"}>
            <Heading as="h6">Taux autorisé</Heading>
          </Flex>
        ),
        meta: { width: "100px" },
      }),
      table3ColumnHelper.accessor("tax_frequency_name", {
        cell: (info) => (
          <Flex px={{ base: "20px", sm: "24px" }}>
            <Text>{info.getValue<string>()}</Text>
          </Flex>
        ),
        header: (info) => (
          <Flex p="14px" px={"40px"}>
            <Heading as="h6">Périodicité</Heading>
          </Flex>
        ),
        meta: { width: "132px" },
      }),
      table3ColumnHelper.accessor("id", {
        cell: (info) => (
          <HStack gap={"2px"} width={"100%"} justifyContent={"flex-end"}>
            {hasPermission("taxnature", "update") &&<IconButton
              variant="outline"
              colorScheme="gray_stroke_0"
              aria-label="Edit"
              size={"sm"}
              icon={<EditIcon />}
              borderRadius={"4px"}
              onClick={() => navigate(`edit/${info.getValue()}`)}
            />}
            {hasPermission("taxnature", "get") &&<IconButton
              variant="outline"
              colorScheme="gray_stroke_0"
              aria-label="view"
              size={"sm"}
              icon={<ViewIcon />}
              borderRadius={"4px"}
              onClick={() => handleViewItem(info.row.original)}
            />}
            {hasPermission("taxnature", "delete") && (
              <IconButton
                variant="outline"
                colorScheme="red_origin"
                aria-label="delete"
                size={"sm"}
                icon={<DeleteIcon />}
                borderRadius={"4px"}
                onClick={() => {
                  setActiveItem(info.row.original);
                  onConfirmOpen();
                }}
                isDisabled={isDeleting && activeItem?.id === info.row.original.id}
                isLoading={isDeleting && activeItem?.id === info.row.original.id}
              />
            )}
          </HStack>
        ),
        header: (info) => (
          <Flex width={"100%"} justifyContent={"flex-end"} px="15px" py="14px">
            <Heading as="h6">Action</Heading>
          </Flex>
        ),
        meta: { width: "100px" },
      }),
    ];
  }, [navigate, handleViewItem, hasPermission, isDeleting, activeItem, onConfirmOpen]);

  return (
    <>
      <Flex
        flexDirection="column"
        alignItems="start"
        px={{ base: "20px", sm: "24px" }}
        minHeight={"100vh"}
      >
        <Flex gap="4px" alignSelf="stretch" flexDirection="column">
          <Flex
            justifyContent="center"
            alignItems="center"
            flexDirection={{ base: "column", sm: "row" }}
          >
            <Heading size="text2xl" as="h5" fontFamily="Arimo">
            Nature de la Créance
            </Heading>
            <Flex
              pl="56px"
              pr="130px"
              gap="13px"
              flex={1}
              justifyContent="flex-end"
              alignItems="center"
              alignSelf={{ base: "stretch", sm: "auto" }}
              flexDirection={{ base: "column", sm: "row" }}
              px={{ md: 0, base: "20px" }}
            >
              
              {hasPermission("taxnature", "create") &&<Button
                size="xl"
                variant="outline"
                colorScheme="red_origin"
                leftIcon={
                  <Image
                    src="images/img_handshake_deal_svgrepo_com_red_origin.svg"
                    alt="Handshake-deal Svgrepo.com"
                  />
                }
                fontWeight={700}
                gap="8px"
                minW="128px"
                borderRadius="8px"
                as={Link}
                href="/tax-natures/add"
              >
                Ajouter une nature de règlement
              </Button>}
            </Flex>
          </Flex>
        </Flex>
        <Box
          mt="16px"
          bg="white"
          w={{ md: "100%", base: "100%" }}
          borderRadius="10px"
        >
          <Flex
            bg="white"
            justifyContent="center"
            alignItems="center"
            px="12px"
            py="16px"
            flexDirection={{ md: "row", base: "column" }}
          >
            <Flex
              flex={1}
              alignItems="center"
              alignSelf={{ md: "auto", base: "stretch" }}
              flexDirection={{ base: "column", sm: "row" }}
            >
              <InputGroup w={{ base: "100%", sm: "30%" }}>
                
                <Input
                  placeholder={`Rechercher une nature de règlement`}
                  value={searchBarValue34}
                  onChange={(event:any) => setSearchBarValue34(event.currentTarget.value)}
                  gap="8px"
                  borderWidth="1px"
                  borderRadius="8px"
                />
                <InputRightElement>
                  {searchBarValue34?.length > 0 ? (
                    <CloseIcon onClick={handleClearSearch} />
                  ) : null}
                </InputRightElement>
              </InputGroup>
                <IconButton onClick={handleSearch} ml={2} borderRadius={"10px"} py={"19px"} border={"1px solid #e2e4e6"} aria-label="search" icon={<SearchIcon/>} justifyContent={"center"} alignItems={"center"} />
            </Flex>
            <Image
              src="images/img_dot_menu.svg"
              alt="Menu Image"
              h="24px"
              w={{ md: "24px", base: "100%" }}
            />
          </Flex>
          <ReactTable
            size="sm"
            headerCellProps={{ bg: "white" }}
            tableProps={{
              borderColor: "neutral.600",
              borderTopWidth: "1px",
              borderStyle: "solid",
            }}
            columns={table3Columns as any}
            data={taxNatures}
          />
          <Pagination
            pageIndex={page}
            pageSize={pageSize}
            setNextPageIndex={handleSetNextPage}
            setPrevPageIndex={handleSetPrevPage}
            setPageSize={setPageSize}
            jumpToPage={handleJumpToPage}
            numberOfPages={taxNatures && taxNatures.length > 0 && taxNatures[0].num_of_pages}
          />
        </Box>
      </Flex>
      <AppViewDrawer isOpen={isOpen} onOpen={onOpen} onClose={onClose}>
        <DrawerHeader>Fiche Nature de la Créance</DrawerHeader>

        <DrawerBody>
          <VStack alignItems={"flex-start"} gap={"15px"}>
            <HStack>
              {hasPermission("taxnature", "update") && <Button
                size="xl"
                variant="outline"
                colorScheme="green_360"
                leftIcon={<EditIcon />}
                fontWeight={700}
                gap="2px"
                borderRadius="8px"
                as={Link}
                href={`/tax-natures/edit/${activeItem?.id}`}
              >
                Modifier
              </Button>}
              {hasPermission("taxnature", "delete") && (
                <Button
                  size="xl"
                  variant="outline"
                  colorScheme="red_origin"
                  leftIcon={<DeleteIcon />}
                  fontWeight={700}
                  gap="8px"
                  borderRadius="8px"
                  onClick={() => {
                    onConfirmOpen();
                  }}
                  isDisabled={isDeleting}
                  isLoading={isDeleting}
                >
                  Supprimer
                </Button>
              )}
            </HStack>
            <AppViewListItem label="Nature de règlement" value={activeItem?.name} />
            {activeItem?.amount && <AppViewListItem label="Montant de la créance" value={activeItem?.amount} />}
            {activeItem?.rate > 0 && <AppViewListItem label="Taux autorisé" value={activeItem?.rate} />}
            <AppViewListItem label="Périodicité" value={activeItem?.tax_frequency_name} />
            <AppViewListItem label="Institution" value={activeItem?.institution_name} />
            <AppViewListItem label="Zone" value={activeItem?.sub_area_id.map(obj => obj.name).join(', ')} />
          </VStack>
        </DrawerBody>
      </AppViewDrawer>
      <AlertDialog isOpen={isConfirmOpen} leastDestructiveRef={cancelRef} onClose={onConfirmClose}>
        <AlertDialogOverlay>
          <AlertDialogContent borderRadius="16px" boxShadow="2xl">
            <AlertDialogHeader fontSize="lg" fontWeight="bold" display="flex" alignItems="center" gap={2}>
              <WarningTwoIcon color="red.400" />
              Confirmation de suppression
            </AlertDialogHeader>

            <AlertDialogBody fontSize="xl" color="gray.700" textAlign="center">
              <VStack spacing={3} align="center">
                <WarningTwoIcon boxSize={16} color="red.400" />
                <Text fontSize="xl">
                  Voulez-vous vraiment supprimer la nature de taxe « {activeItem?.name} » ?
                </Text>
                <Text fontSize="xl">
                  Cette action est irréversible.
                </Text>
              </VStack>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onConfirmClose} variant="outline" colorScheme="gray_stroke_0" leftIcon={<CloseIcon boxSize={3} />} borderRadius="8px" isDisabled={isDeleting}>
                Annuler
              </Button>
              <Button colorScheme="red_origin" ml={3} leftIcon={<DeleteIcon />} borderRadius="8px" isLoading={isDeleting} loadingText="Suppression..." onClick={async () => {
                if (!activeItem?.id) return;
                try {
                  setIsDeleting(true);
                  await deleteTaxNature(activeItem.id);
                  dispatch(
                    showToast({
                      toast,
                      message: "Nature de taxe supprimée avec succès",
                      status: toastTypes.success,
                    })
                  );
                  onConfirmClose();
                  onClose();
                  mutate(
                    getTaxNatures(
                      page,
                      searchBarValue34,
                      String(user?.user?.institution_id || "")
                    )
                  );
                } finally {
                  setIsDeleting(false);
                }
              }}>
                Supprimer
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}
